// Scrolls only the nearest ancestor that scrolls vertically, and only as far as the panel's bottom needs to come
// into view. Not scrollIntoView: that scrolls every ancestor, including one with overflow: hidden (which clips but
// can still be scrolled from script), and could leave content shifted inside it with no way for the user to
// scroll it back.
//
// Never scrolls so far that the element just before the panel (the row that opened it) leaves the top of the
// scroller, or goes under the part of that top the scroller's scroll-padding-top says is covered (by a sticky
// header, say): a panel taller than the visible area shows as much as fits below its row.
//
// Does nothing for a panel that is not displayed (display: none on it or an ancestor). One moved out of view with a
// transform is still displayed, so its scroller's position may be adjusted; nothing outside that scroller moves.
export function revealPanelBottom(panel: HTMLElement | null) {
  if (!panel || panel.getClientRects().length === 0) {
    return;
  }
  const scroller = findVerticalScroller(panel);
  if (!scroller) {
    return;
  }
  const panelRect = panel.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const overflowBelow = panelRect.bottom - scrollerRect.bottom;
  if (overflowBelow <= 0) {
    return;
  }
  const row = panel.previousElementSibling ?? panel;
  const visibleTop = scrollerRect.top + (parseFloat(getComputedStyle(scroller).scrollPaddingTop) || 0);
  const distance = Math.min(overflowBelow, Math.max(0, row.getBoundingClientRect().top - visibleTop));
  if (distance <= 0) {
    return;
  }
  scroller.scrollTo({ top: scroller.scrollTop + distance, behavior: 'smooth' });
}

// The nearest ancestor that scrolls vertically, or none when a clipping ancestor (overflow: hidden or clip) or the
// page body comes first
export function findVerticalScroller(element: HTMLElement): HTMLElement | undefined {
  for (
    let ancestor = element.parentElement;
    ancestor && ancestor !== document.body;
    ancestor = ancestor.parentElement
  ) {
    const { overflowY } = getComputedStyle(ancestor);
    if (overflowY === 'hidden' || overflowY === 'clip') {
      return undefined;
    }
    if ((overflowY === 'auto' || overflowY === 'scroll') && ancestor.scrollHeight > ancestor.clientHeight) {
      return ancestor;
    }
  }
  return undefined;
}
